import { useState, useEffect } from "react";
import { Dropdown, MenuProps, Spin, Tag } from "antd";
import { Trans } from "react-i18next";
import CommonButton from "@components/CommonButton";
import Image from "@components/Image";
import libraryService from "@apis/libraryService";
import { IconDelete, IconClose, IconMore } from "@icons/index";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { fetchVideoGenerateList, hideTrashDisplay, statusConfig } from "@pages/library/librarySlice";
import { useKeyup } from "@hooks/utils";
import VideoCover from "@layouts/VideoCover";
import NoResult from "@components/Section/layouts/NoResult";

// 回收站卡片组件
const TrashCard = ({
  record,
  onRestore,
  onDelete,
}: {
  record: any;
  onRestore: (id: string) => void;
  onDelete: (id: string) => void;
}) => {
  const status = record?.jobItem?.status;
  const context = record?.jobItem?.context || {};
  const config = (statusConfig as any)[status || "queued"];

  const menuItems: MenuProps["items"] = [
    {
      key: "restore",
      label: (
        <div
          className="w-full h-full flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            onRestore(record.id);
          }}
        >
          <Trans>Restore</Trans>
        </div>
      ),
    },
    {
      key: "delete",
      label: (
        <div
          className="w-full h-full flex items-center text-red-500"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(record.id);
          }}
        >
          <Trans>Delete forever</Trans>
        </div>
      ),
    },
  ];

  // 渲染封面区域
  const renderCover = () => {
    return (
      <div className="relative w-full aspect-3/4">
        <VideoCover
          cover={context.coverImageUrl}
          className="w-full aspect-3/4 rounded-0! border-none! rounded-none!"
          videoUrl={context?.videoUrl}
          aspectRatio={context?.aspectRatio}
          showAdIcon={false}
          videoIconSize="size48"
          adIconSize="size24"
          videoType="video"
        ></VideoCover>
      </div>
    );
  };

  return (
    <div className="kalo-white-card flex flex-col w-full group relative p-0! overflow-hidden cursor-pointer">
      <div className="relative w-full aspect-3/4">{renderCover()}</div>

      <div className="p-[16px]">
        {/* 商品信息 */}
        <div className="pb-[12px] flex items-start gap-[8px] border-b border-kds-border">
          {context.productCoverImageUrl ? (
            <Image className="w-[32px] h-[32px] shrink-0 rounded-[4px]" url={context.productCoverImageUrl} />
          ) : (
            <div className="w-[32px] h-[32px] shrink-0 rounded-[4px] bg-kds-bg-soft border border-kds-border" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-kds-fg leading-[18px] line-clamp-2 m-0">{context.productTitle || "-"}</p>
          </div>
        </div>

        {/* 状态栏 */}
        <div className="flex items-center justify-between pt-[12px]">
          <Tag color={config.color} className="rounded-full!">
            <Trans>{config.label}</Trans>
          </Tag>
        </div>
      </div>

      {/* 右上角恢复菜单 */}
      <Dropdown trigger={["hover"]} menu={{ items: menuItems }} placement="bottomLeft">
        <div
          className="absolute right-[11px] top-[11px] w-[24px] h-[24px] gap-[3px] cursor-pointer hidden group-hover:flex"
          onClick={(e) => e.stopPropagation()}
        >
          <IconMore className="w-[24px] h-[24px]" />
        </div>
      </Dropdown>
    </div>
  );
};

const GlobalTrashModal = () => {
  const dispatch = useAppDispatch();
  const { trashDisplay } = useAppSelector((state) => state.libraryReducer);
  const [trashList, setTrashList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [emptyLoading, setEmptyLoading] = useState(false);

  // 控制 body 滚动
  if (trashDisplay) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "overlay";
  }

  const handleClose = () => {
    dispatch(hideTrashDisplay());
    dispatch(fetchVideoGenerateList({}));
  };

  // ESC 键关闭
  useKeyup(handleClose);

  const fetchTrashList = async () => {
    setLoading(true);
    try {
      const result = await libraryService.fetchTrashList<any[]>();
      setTrashList(result || []);
    } catch (error) {
      console.error("Failed to fetch trash list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trashDisplay) {
      fetchTrashList();
    }
  }, [trashDisplay]);

  const handleEmptyTrash = async () => {
    setEmptyLoading(true);
    try {
      await libraryService.emptyTrash();
      setTrashList([]);
    } catch (error) {
      console.error("Failed to empty trash:", error);
    } finally {
      setEmptyLoading(false);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await libraryService.restoreFromTrash(id);
      setTrashList((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to restore item:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await libraryService.deleteFromTrash(id);
      setTrashList((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete item:", error);
    }
  };

  if (trashDisplay) {
    return createPortal(
      <div className="fixed inset-0 z-1000 bg-kds-bg-soft">
        <div className="absolute inset-0 bg-kds-bg-layout overflow-hidden">
          {/* 标题栏 */}
          <div className="flex items-center gap-[16px] px-[32px] py-[12px] border-b border-kds-border">
            <CommonButton className="w-[32px] h-[32px] p-0!" onClick={handleClose}>
              <IconClose className="w-[24px]! h-[24px]!" />
            </CommonButton>
            <span className="text-kds-fg font-semibold text-[16px]">
              <Trans>Trash</Trans>
            </span>
          </div>

          <div className="px-[32px] py-[16px]">
            {/* 提示和操作栏 */}
            <div className="flex justify-between items-center mb-[12px]">
              <span className="text-kds-fg-subtle text-[14px] font-normal">
                <Trans
                  components={{ span: <span className="text-kds-fg" /> }}
                  defaults="Items are permanently deleted <span>30 days</span> after being moved to the Trash."
                />
              </span>
              <CommonButton onClick={handleEmptyTrash} loading={emptyLoading} disabled={trashList.length === 0}>
                <IconDelete className="w-[16px] h-[16px] [&_path]:stroke-current" />
                <Trans>Empty Trash</Trans>
              </CommonButton>
            </div>

            {/* 内容区域 */}
            <Spin spinning={loading}>
              <div className="h-[calc(100vh-150px)] overflow-y-auto scrollbar-none">
                {trashList.length > 0 ? (
                  <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
                    {trashList.map((item) => (
                      <TrashCard key={item.id} record={item} onRestore={handleRestore} onDelete={handleDelete} />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[400px]">
                    <NoResult blankTip="Trash is empty" />
                  </div>
                )}
              </div>
            </Spin>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return null;
};

export default GlobalTrashModal;
